import { useQuery } from '@apollo/client/react';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import TextField from '@mui/material/TextField';
import { useState } from 'react';
import ErrorBox from '../../core/components/ErrorBox';
import assert from '../../core/util/assert';
import { bookingStatsQuery, BookingStatsQueryResult } from '../../main/queries';
import DayBookingsList from './DayBookingsList';
import YearlyBookingHeatmap from './YearlyBookingHeatmap';

interface Booking {
    id: number;
    guestName: string;
    checkin: string;
    checkout: string;
    revenue: number;
    currency: string;
    cleaningFee: number;
}

const StatsHeatmap = () => {
    const [selectedDay, setSelectedDay] = useState<{
        monthDay: string;
        bookings: Booking[];
    } | null>(null);
    const [yearsBack, setYearsBack] = useState<number>(3);

    const { loading, error, data } = useQuery<BookingStatsQueryResult>(
        bookingStatsQuery,
        {
            variables: { projected: true },
        }
    );

    if (loading) {
        return (
            <Box display="flex" flexDirection="column" gap={3} p={5}>
                <Skeleton variant="text" height={50} />
                <Skeleton variant="rectangular" height={400} />
            </Box>
        );
    }

    if (error) return <ErrorBox error={error} />;

    assert(data);

    const { bookings } = data.stats;

    const currentYear = new Date().getFullYear();
    const cutoffYear = currentYear - yearsBack;
    const filteredBookings = bookings.filter(booking => {
        const bookingYear = Number(booking.checkin.slice(0, 4));
        return bookingYear >= cutoffYear;
    });

    const handleDayClick = (monthDay: string, dayBookings: Booking[]) => {
        setSelectedDay({ monthDay, bookings: dayBookings });
    };

    return (
        <Box display="flex" flexDirection="column" gap={3} p={5}>
            <Box display="flex" alignItems="center" gap={2}>
                <TextField
                    select
                    label="Years to include"
                    value={yearsBack}
                    onChange={e => setYearsBack(Number(e.target.value))}
                    size="small"
                    sx={{ width: 200 }}
                >
                    <MenuItem value={1}>Last 1 year</MenuItem>
                    <MenuItem value={2}>Last 2 years</MenuItem>
                    <MenuItem value={3}>Last 3 years</MenuItem>
                    <MenuItem value={5}>Last 5 years</MenuItem>
                    <MenuItem value={10}>Last 10 years</MenuItem>
                    <MenuItem value={100}>All years</MenuItem>
                </TextField>
            </Box>
            <Box display="flex" flexDirection="row" gap={3}>
                <Box flex={1}>
                    <YearlyBookingHeatmap
                        bookings={filteredBookings}
                        onDayClick={handleDayClick}
                    />
                </Box>
                {selectedDay && (
                    <Paper
                        elevation={2}
                        sx={{
                            width: 400,
                            maxHeight: '80vh',
                            overflow: 'auto',
                        }}
                    >
                        <DayBookingsList
                            monthDay={selectedDay.monthDay}
                            bookings={selectedDay.bookings}
                        />
                    </Paper>
                )}
            </Box>
        </Box>
    );
};

export default StatsHeatmap;
